import { z } from 'zod';

import type { AppFailure } from '@/utils/exceptions/appFailure';
import { FailureCatalog } from '@/utils/exceptions/failureCatalog';

/** Откуда запись в журнале. */
export const DiagnosticsKind = {
  /** Сбой операции: код, статус, текст сервера, подсказка инженеру. */
  failure: 'failure',

  /** Показанный тост (в том числе без кода, вроде «Не распознано»). */
  toast: 'toast',

  /** HTTP-запрос: метод, адрес без query, статус, длительность. */
  request: 'request',

  /** SSE-поток: сколько ждали заголовков, чем кончился. */
  stream: 'stream',

  /** Токены планшета: вход, refresh, истечение. */
  session: 'session',

  /** Прочее: запуск, сеть, переходы. */
  event: 'event',
} as const;

export type DiagnosticsKind =
  (typeof DiagnosticsKind)[keyof typeof DiagnosticsKind];

/** Аналог `DiagnosticsKind.values`: порядок объявления сохранён. */
export const DIAGNOSTICS_KIND_VALUES: readonly DiagnosticsKind[] =
  Object.values(DiagnosticsKind);

/** Запись журнала. Неизменяемая — как `@immutable` в Dart. */
export interface DiagnosticsEntry {
  readonly time: Date;
  readonly kind: DiagnosticsKind;
  readonly title: string;
  readonly code?: string;
  readonly details?: string;
  readonly durationMs?: number;
  readonly isError: boolean;
}

/**
 * Схема сохранённой записи.
 *
 * В хранилище лежат короткие ключи (`t`, `k`, `ms`, `err`) — формат тот же,
 * что писала Dart-версия. Длинные имена полей выдаёт `transform`: во всём
 * порте короткие ключи живут только внутри схемы, наружу выходит
 * [DiagnosticsEntry].
 */
const diagnosticsEntrySchema = z
  .object({
    t: z.number().int(),
    k: z.enum(DiagnosticsKind),
    title: z.string(),
    code: z.string().nullish(),
    details: z.string().nullish(),
    ms: z.number().int().nullish(),
    // `.optional()` обязателен: `diagnosticsEntryToJson` пишет `err` только
    // для записи об ошибке, а голый `z.unknown()` в zod 4 требует наличия
    // ключа. Без этого после перезапуска из журнала восстанавливались одни
    // сбои, а запросы и события — те, что к ним привели, — пропадали.
    err: z.unknown().optional(),
  })
  .transform(
    (json): DiagnosticsEntry => ({
      time: new Date(json.t),
      kind: json.k,
      title: json.title,
      code: json.code ?? undefined,
      details: json.details ?? undefined,
      durationMs: json.ms ?? undefined,
      isError: json.err === true,
    }),
  );

export function diagnosticsEntryToJson(
  entry: DiagnosticsEntry,
): Record<string, unknown> {
  return {
    t: entry.time.getTime(),
    k: entry.kind,
    title: entry.title,
    ...(entry.code !== undefined ? { code: entry.code } : {}),
    ...(entry.details !== undefined ? { details: entry.details } : {}),
    ...(entry.durationMs !== undefined ? { ms: entry.durationMs } : {}),
    ...(entry.isError ? { err: true } : {}),
  };
}

/** Битая запись журнал не ломает — она просто не восстанавливается. */
export function diagnosticsEntryTryFromJson(
  json: unknown,
): DiagnosticsEntry | null {
  const parsed = diagnosticsEntrySchema.safeParse(json);
  return parsed.success ? parsed.data : null;
}

/** Сколько последних записей держим. */
const CAPACITY = 300;

const PREFS_KEY = 'diagnosticsLog';

/** Запись на диск не на каждое событие: запросы идут пачками. */
const SAVE_DELAY_MS = 2000;

/** С этого кода ответ сервера считается сбоем. */
const HTTP_ERROR_STATUS = 400;

/**
 * Журнал для разработчика: последние события на самом планшете.
 *
 * Работает и в release-сборке — открывается скрытым входом (5 тапов по
 * версии на экране языка + PIN). Переживает перезапуск: хранится в
 * `localStorage`. Секретов не пишет: адреса без query, токены и ключи
 * вычищаются из любого текста ([scrub]).
 *
 * В Dart это `ChangeNotifier`, и экран подписывался через `ListenableBuilder`.
 * Здесь та же роль у пары [subscribe]/[getSnapshot] — они отдаются в
 * `useSyncExternalStore`.
 */
export class DiagnosticsLog {
  private constructor() {}

  static instance: DiagnosticsLog = new DiagnosticsLog();

  /** Свежий экземпляр для тестов. */
  static testInstance(): DiagnosticsLog {
    return new DiagnosticsLog();
  }

  /** Сколько последних записей держим. */
  static readonly capacity = CAPACITY;

  /** Старые сверху — как `_entries` в Dart. Массив не меняется на месте. */
  #entries: readonly DiagnosticsEntry[] = [];

  /**
   * Кеш перевёрнутого списка: `useSyncExternalStore` требует, чтобы снимок
   * не менял ссылку, пока не изменилось состояние.
   */
  #reversed: readonly DiagnosticsEntry[] | null = null;

  #storage: Storage | null = null;
  #saveTimer: ReturnType<typeof setTimeout> | null = null;
  readonly #listeners = new Set<() => void>();

  /** Новые сверху. */
  get entries(): readonly DiagnosticsEntry[] {
    const cached = this.#reversed;
    if (cached !== null) return cached;
    const reversed = [...this.#entries].reverse();
    this.#reversed = reversed;
    return reversed;
  }

  /** Подписка для `useSyncExternalStore` — замена `ListenableBuilder`. */
  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  };

  /** Снимок для `useSyncExternalStore`. */
  getSnapshot = (): readonly DiagnosticsEntry[] => this.entries;

  /**
   * Поднимает сохранённые записи. Сбой чтения журнал не ломает — он
   * просто начнётся с чистого листа.
   */
  async initialize(): Promise<void> {
    // Старт при сбое повторяется каждые 5 с — повторная загрузка затёрла бы
    // записи этих попыток.
    if (this.#storage !== null) return;
    try {
      // Сам доступ к localStorage бросает, когда хранилище запрещено
      // настройками браузера, — поэтому он внутри try.
      const storage = window.localStorage;
      this.#storage = storage;
      const raw = storage.getItem(PREFS_KEY);
      if (raw === null) return;
      const list: unknown = JSON.parse(raw);
      if (!Array.isArray(list)) return;
      const restored = list
        .map((item: unknown) => diagnosticsEntryTryFromJson(item))
        .filter((entry): entry is DiagnosticsEntry => entry !== null);
      // Записи, сделанные до загрузки, идут после сохранённых.
      const early = this.#entries;
      this.#setEntries(trimToCapacity([...restored, ...early]));
    } catch {
      // Битый журнал — начинаем с того, что есть в памяти.
    }
  }

  add(entry: DiagnosticsEntry): void {
    this.#setEntries(trimToCapacity([...this.#entries, entry]));
    this.#scheduleSave();
    this.#notifyListeners();
  }

  /**
   * Сбой операции. [shown] — как его увидел человек (может отличаться:
   * «нет сети» вместо «сервер недоступен»), [message] — текст тоста;
   * `undefined` — сбой без тоста (подсказки, список планшетов).
   */
  failure(
    failure: AppFailure,
    { shown, message }: { shown?: AppFailure; message?: string } = {},
  ): void {
    const visible = shown ?? failure;
    const code =
      visible === failure ? failure.code : `${visible.code} (${failure.code})`;
    this.add({
      time: new Date(),
      kind: DiagnosticsKind.failure,
      title: message ?? 'без тоста',
      code,
      details: DiagnosticsLog.scrub(
        `${failure.diagnostics}\n\n${FailureCatalog.hint(visible)}`,
      ),
      isError: true,
    });
  }

  toast({
    type,
    title,
    code,
  }: {
    type: string;
    title?: string;
    code?: string;
  }): void {
    this.add({
      time: new Date(),
      kind: DiagnosticsKind.toast,
      title: DiagnosticsLog.scrub(title ?? '—'),
      code,
      details: `тост: ${type}`,
      isError: type === 'error',
    });
  }

  request({
    method,
    uri,
    status,
    durationMs,
    error,
    note,
  }: {
    method: string;
    uri: URL | string;
    status?: number;
    durationMs?: number;
    error?: string;
    note?: string;
  }): void {
    const failed =
      error !== undefined ||
      (status !== undefined && status >= HTTP_ERROR_STATUS);
    this.add({
      time: new Date(),
      kind: DiagnosticsKind.request,
      title: `${method} ${DiagnosticsLog.safeUrl(uri)}`,
      code:
        status !== undefined
          ? status.toString()
          : error === undefined
            ? undefined
            : 'нет ответа',
      details: ifEmptyUndefined(
        [
          ...(error !== undefined ? [DiagnosticsLog.scrub(error)] : []),
          ...(note !== undefined ? [DiagnosticsLog.scrub(note)] : []),
        ].join('\n'),
      ),
      durationMs,
      isError: failed || note !== undefined,
    });
  }

  stream({
    title,
    code,
    durationMs,
    details,
    isError = false,
  }: {
    title: string;
    code?: string;
    durationMs?: number;
    details?: string;
    isError?: boolean;
  }): void {
    this.add({
      time: new Date(),
      kind: DiagnosticsKind.stream,
      title: DiagnosticsLog.scrub(title),
      code,
      details: details === undefined ? undefined : DiagnosticsLog.scrub(details),
      durationMs,
      isError,
    });
  }

  session(message: string): void {
    this.add({
      time: new Date(),
      kind: DiagnosticsKind.session,
      title: DiagnosticsLog.scrub(message),
      isError: false,
    });
  }

  event(message: string, { isError = false }: { isError?: boolean } = {}): void {
    this.add({
      time: new Date(),
      kind: DiagnosticsKind.event,
      title: DiagnosticsLog.scrub(message),
      isError,
    });
  }

  clear(): void {
    this.#setEntries([]);
    this.#scheduleSave();
    this.#notifyListeners();
  }

  /** Весь журнал одним текстом — для копирования. */
  export(): string {
    return this.entries
      .map((e) => {
        const parts = [
          toIso8601Local(e.time),
          e.kind,
          ...(e.code !== undefined ? [e.code] : []),
          e.title,
          ...(e.durationMs !== undefined ? [`${e.durationMs}ms`] : []),
        ];
        const details = e.details === undefined ? '' : `\n    ${e.details}`;
        return `${parts.join(' | ')}${details}`;
      })
      .join('\n');
  }

  /** Сохраняет сразу — перед перезапуском и сбросом, в тестах. */
  async flush(): Promise<void> {
    if (this.#saveTimer !== null) clearTimeout(this.#saveTimer);
    this.#saveTimer = null;
    this.#save();
  }

  #setEntries(next: readonly DiagnosticsEntry[]): void {
    this.#entries = next;
    this.#reversed = null;
  }

  #scheduleSave(): void {
    if (this.#storage === null) return;
    if (this.#saveTimer !== null) clearTimeout(this.#saveTimer);
    this.#saveTimer = setTimeout(() => {
      this.#save();
    }, SAVE_DELAY_MS);
  }

  #save(): void {
    const storage = this.#storage;
    if (storage === null) return;
    try {
      storage.setItem(
        PREFS_KEY,
        JSON.stringify(this.#entries.map(diagnosticsEntryToJson)),
      );
    } catch {
      // Журнал — не повод ронять приложение: переполненная квота
      // localStorage не должна выбрасывать из экрана.
    }
  }

  #notifyListeners(): void {
    // Копия набора: слушатель может отписаться прямо во время обхода.
    for (const listener of [...this.#listeners]) listener();
  }

  /**
   * Адрес без query и фрагмента: в query ходит `refresh_token`.
   *
   * Dart принимал `Uri`; здесь это `URL` либо строка — HTTP-слой порта
   * работает и с тем и с другим.
   */
  static safeUrl(uri: URL | string): string {
    if (typeof uri === 'string') {
      const parsed = tryParseUrl(uri);
      if (parsed === null) {
        // Адрес не разобрался (относительный путь) — режем query сами.
        const cut = uri.indexOf('?');
        return cut < 0 ? uri : `${uri.slice(0, cut)}?…`;
      }
      return DiagnosticsLog.safeUrl(parsed);
    }
    // `uri.host` — это `authority` без userInfo: логинов в адресах
    // приложения не бывает, а пароль в журнале нам и не нужен.
    const base = `${uri.protocol.slice(0, -1)}://${uri.host}${uri.pathname}`;
    return uri.search.length > 0 ? `${base}?…` : base;
  }

  /** Вычищает токены, ключи и пароли из произвольного текста. */
  static scrub(text: string): string {
    let result = text;
    for (const [pattern, replacement] of SECRET_PATTERNS) {
      result = result.replace(pattern, replacement);
    }
    return result;
  }
}

/**
 * Порядок важен — он тот же, что у `_secretPatterns` в Dart. Подстановка
 * `$1` работает в `String.replace` так же, как `replaceAllMapped` в Dart.
 */
const SECRET_PATTERNS: readonly (readonly [RegExp, string])[] = [
  [/(refresh_token|token|api_key|password)=[^&\s]+/g, '$1=***'],
  [/Bearer\s+[^\s,"]+/g, 'Bearer ***'],
  // Токены планшета (itsdangerous): «.eJ…» или «eJ…» с точками.
  [/\.?eJ[\w-]+\.[\w-]+\.[\w-]+/g, '***'],
  [/"(token|refresh_token|api_key|password)"\s*:\s*"[^"]*"/g, '"$1": "***"'],
];

/** Оставляет последние [CAPACITY] записей, не трогая исходный массив. */
function trimToCapacity(
  entries: readonly DiagnosticsEntry[],
): readonly DiagnosticsEntry[] {
  const extra = entries.length - CAPACITY;
  return extra > 0 ? entries.slice(extra) : entries;
}

/** Порт расширения `String.ifEmptyNull` из Dart-файла. */
function ifEmptyUndefined(text: string): string | undefined {
  return text.length === 0 ? undefined : text;
}

/**
 * Местное время без указания зоны — ровно то, что отдавал
 * `DateTime.toIso8601String()` для локального `DateTime`.
 * `Date.toISOString()` здесь не годится: он переводит время в UTC.
 */
function toIso8601Local(time: Date): string {
  const pad = (value: number, length = 2): string =>
    value.toString().padStart(length, '0');
  const date = `${time.getFullYear()}-${pad(time.getMonth() + 1)}-${pad(time.getDate())}`;
  const clock = `${pad(time.getHours())}:${pad(time.getMinutes())}:${pad(time.getSeconds())}`;
  return `${date}T${clock}.${pad(time.getMilliseconds(), 3)}`;
}

/** `null` вместо исключения: разбор адреса не должен ронять журнал. */
function tryParseUrl(value: string): URL | null {
  try {
    return new URL(value);
  } catch {
    return null;
  }
}
